import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useMenuTitle } from '../../hooks/useMenuTitle';
import { FaQuestionCircle } from 'react-icons/fa';

export default function RoleManagement() {
  const menuTitle = useMenuTitle();
  const supabase = useSupabaseClient();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isHelpOpen, 
    onOpen: onHelpOpen, 
    onClose: onHelpClose 
  } = useDisclosure();
  
  const [roles, setRoles] = useState([]);
  const [editingRole, setEditingRole] = useState(null);

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast({
        title: 'Error fetching roles',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Handle role modal
  const handleOpenRoleModal = (role = null) => {
    setEditingRole(role || { name: '', description: '' });
    onOpen();
  };

  const handleCloseRoleModal = () => {
    setEditingRole(null);
    onClose();
  };

  // Save role
  const handleSaveRole = async () => {
    try {
      if (!editingRole.name) {
        toast({
          title: 'Validation Error',
          description: 'Role name is required',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      if (editingRole.id) {
        // Update existing role
        const { error } = await supabase
          .from('roles')
          .update({
            name: editingRole.name,
            description: editingRole.description,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingRole.id);

        if (error) throw error;
      } else {
        // Create new role
        const { error } = await supabase
          .from('roles')
          .insert({
            name: editingRole.name,
            description: editingRole.description,
          });

        if (error) throw error;
      }

      handleCloseRoleModal();
      fetchRoles();
      toast({
        title: 'Success',
        description: `Role ${editingRole.id ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: 'Error saving role',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId) => {
    try {
      const { error } = await supabase
        .from('roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;

      fetchRoles();
      toast({
        title: 'Success',
        description: 'Role deleted successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error deleting role:', error);
      toast({
        title: 'Error deleting role',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Render help modal
  const renderHelpModal = () => (
    <Modal isOpen={isHelpOpen} onClose={onHelpClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="600px">
        <ModalHeader>Role Management Help</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Role Management allows you to create and manage user roles that control access to different parts of the application.
              Each role can be assigned specific permissions and menu items.
            </Text>

            <Text fontWeight="semibold">Features:</Text>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>• Create new roles with custom names and descriptions</Text>
              <Text>• Edit existing roles to update their properties</Text>
              <Text>• Delete roles that are no longer needed</Text>
              <Text>• View all existing roles and their descriptions</Text>
            </VStack>

            <Text fontWeight="semibold">Important Notes:</Text>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>• The SuperAdmin role cannot be modified or deleted</Text>
              <Text>• Deleting a role will remove access for all users assigned to that role</Text>
              <Text>• Role names must be unique</Text>
              <Text>• Changes to roles take effect immediately</Text>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  return (
    <Box p={4}>
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Text fontSize="2xl" fontWeight="bold">{menuTitle}</Text>
          <HStack>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={() => handleOpenRoleModal()}
            >
              Add Role
            </Button>
            <Tooltip label="Help" placement="left">
              <IconButton
                icon={<Icon as={FaQuestionCircle} />}
                variant="ghost"
                onClick={onHelpOpen}
                aria-label="Help"
              />
            </Tooltip>
          </HStack>
        </HStack>

        {/* Role list */}
        <VStack spacing={4} align="stretch">
          {roles.map((role) => (
            <Box
              key={role.id}
              p={4}
              bg="white"
              borderRadius="md"
              boxShadow="sm"
            >
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">{role.name}</Text>
                  {role.description && (
                    <Text fontSize="sm" color="gray.600">
                      {role.description}
                    </Text>
                  )}
                </VStack>
                <HStack spacing={2}>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    aria-label="Edit Role"
                    isDisabled={role.name.toLowerCase() === 'superadmin'}
                    onClick={() => handleOpenRoleModal(role)}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Delete Role"
                    isDisabled={role.name.toLowerCase() === 'superadmin'}
                    onClick={() => handleDeleteRole(role.id)}
                  />
                </HStack>
              </HStack>
            </Box>
          ))}
        </VStack>

        {/* Role Modal */}
        <Modal isOpen={isOpen} onClose={handleCloseRoleModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingRole?.id ? 'Edit Role' : 'Add New Role'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Role Name</FormLabel>
                  <Input
                    value={editingRole?.name || ''}
                    onChange={(e) =>
                      setEditingRole((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter role name"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input
                    value={editingRole?.description || ''}
                    onChange={(e) =>
                      setEditingRole((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Enter role description"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleCloseRoleModal}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSaveRole}>
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Help Modal */}
        {renderHelpModal()}
      </VStack>
    </Box>
  );
}
